/**
 * Bending-moment and shear-force diagrams.
 *
 * Driven by the same diagram JSON the report renderer consumes, so screen and print
 * cannot disagree (CLAUDE.md 8.3). Colours come from CSS variables, which is what
 * makes the diagram follow the theme switch; the report pipeline overrides those same
 * variables with the fixed print palette.
 *
 * Added over the first version, all from 14.3/14.5:
 *
 * - **A readout.** Hovering or tapping the plot reports M(x) or V(x) at that station,
 *   which is what an engineer actually wants from a BMD -- the peak alone is on the
 *   check table already. Pointer events cover mouse and touch alike, so this is not a
 *   hover-only affordance.
 * - **The peak is marked and labelled**, with its x-station, rather than being a
 *   number in the caption disconnected from the curve.
 * - **Axis ticks**, so the shape can be read quantitatively.
 * - **A data-table fallback** behind a disclosure, because a diagram alone is not
 *   accessible and a screen-reader user still needs the numbers (14.5).
 */
import { useId, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { DiagramData } from '@/api/types'
import { formatQuantity } from '@/lib/format'

interface Props {
  data: DiagramData
  kind: 'moment' | 'shear'
}

const WIDTH = 640
const HEIGHT = 190
const PAD = { top: 22, right: 18, bottom: 34, left: 52 }

export function BeamDiagram({ data, kind }: Props) {
  const { t } = useTranslation(['common', 'report'])
  const svgRef = useRef<SVGSVGElement | null>(null)
  const [probe, setProbe] = useState<{ x: number; value: number } | null>(null)
  const tableId = useId()

  const values = kind === 'moment' ? data.bmd_knm : data.sfd_kn
  const unit = kind === 'moment' ? 'kNm' : 'kN'
  const stroke = kind === 'moment' ? 'var(--diagram-moment)' : 'var(--diagram-shear)'
  const fill = kind === 'moment' ? 'var(--diagram-moment-fill)' : 'var(--diagram-shear-fill)'

  const geometry = useMemo(() => {
    if (!values.length || !data.x_mm.length) return null

    const plotW = WIDTH - PAD.left - PAD.right
    const plotH = HEIGHT - PAD.top - PAD.bottom
    const maxAbs = Math.max(...values.map(Math.abs), 1e-9)
    const baseline = PAD.top + plotH / 2

    const sx = (x: number) => PAD.left + (x / data.span_mm) * plotW
    // Moment is drawn sagging-positive-downwards, the convention engineers expect on
    // a beam elevation; shear keeps the usual positive-up sign.
    const sy = (v: number) => {
      const norm = v / maxAbs
      return kind === 'moment' ? baseline + (norm * plotH) / 2 : baseline - (norm * plotH) / 2
    }

    const points = data.x_mm.map((x, i) => `${sx(x)},${sy(values[i] ?? 0)}`).join(' ')
    const area = `${PAD.left},${baseline} ${points} ${sx(data.span_mm)},${baseline}`

    // The extreme value and where it occurs -- the caption used to give the magnitude
    // with no indication of its station.
    let peakIndex = 0
    for (let i = 1; i < values.length; i++) {
      if (Math.abs(values[i] ?? 0) > Math.abs(values[peakIndex] ?? 0)) peakIndex = i
    }

    return { plotW, plotH, maxAbs, baseline, sx, sy, points, area, peakIndex }
  }, [data, values, kind])

  if (!geometry) return null

  const { baseline, sx, sy, points, area, peakIndex } = geometry
  const peakValue = values[peakIndex] ?? 0
  const peakX = data.x_mm[peakIndex] ?? 0

  /** Map a pointer position to the nearest sample. */
  const onProbe = (event: React.PointerEvent<SVGSVGElement>) => {
    const svg = svgRef.current
    if (!svg) return
    const bounds = svg.getBoundingClientRect()
    // The SVG scales to its container, so convert through the viewBox width.
    const viewX = ((event.clientX - bounds.left) / bounds.width) * WIDTH
    const ratio = (viewX - PAD.left) / (WIDTH - PAD.left - PAD.right)
    if (ratio < 0 || ratio > 1) return setProbe(null)

    const index = Math.round(ratio * (data.x_mm.length - 1))
    setProbe({ x: data.x_mm[index] ?? 0, value: values[index] ?? 0 })
  }

  const titleKey = kind === 'moment' ? 'step.demo.moment' : 'step.demo.shear'

  return (
    <figure className="m-0">
      <figcaption className="mb-1 flex items-baseline justify-between gap-2 text-sm">
        <span className="font-medium">{t(titleKey, { ns: 'report' })}</span>
        <span className="tabular text-text-muted">
          {probe
            ? `x = ${formatQuantity(probe.x / 1000, 'volume', 'm')} · ${formatQuantity(
                probe.value,
                'moment',
                unit,
              )}`
            : formatQuantity(peakValue, 'moment', unit)}
        </span>
      </figcaption>

      {/* Own scroll container: the page body never scrolls horizontally (14.3). */}
      <div className="scroll-x rounded-md border border-border bg-surface">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          width="100%"
          height={HEIGHT}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label={t('diagram.summary', {
            kind: t(titleKey, { ns: 'report' }),
            peak: formatQuantity(peakValue, 'moment', unit),
            span: formatQuantity(data.span_mm / 1000, 'volume', 'm'),
          })}
          aria-describedby={tableId}
          className="block min-w-[340px] touch-pan-y"
          onPointerMove={onProbe}
          onPointerDown={onProbe}
          onPointerLeave={() => setProbe(null)}
        >
          {/* Zero axis */}
          <line
            x1={PAD.left}
            y1={baseline}
            x2={WIDTH - PAD.right}
            y2={baseline}
            stroke="var(--diagram-axis)"
            strokeWidth={1}
          />

          {/* Quarter-point ticks: enough to read the shape, few enough not to clutter. */}
          {[0, 0.25, 0.5, 0.75, 1].map((fraction) => {
            const x = sx(data.span_mm * fraction)
            return (
              <g key={fraction}>
                <line
                  x1={x}
                  y1={baseline - 3}
                  x2={x}
                  y2={baseline + 3}
                  stroke="var(--diagram-axis)"
                  strokeWidth={1}
                />
                <text
                  x={x}
                  y={HEIGHT - 8}
                  fontSize={10}
                  textAnchor={fraction === 0 ? 'start' : fraction === 1 ? 'end' : 'middle'}
                  fill="var(--diagram-axis)"
                >
                  {((data.span_mm * fraction) / 1000).toFixed(fraction === 0 ? 0 : 2)}
                </text>
              </g>
            )
          })}

          <polygon points={area} fill={fill} />
          <polyline points={points} fill="none" stroke={stroke} strokeWidth={2} />

          {/* Peak marker, with its magnitude at its station. */}
          <circle cx={sx(peakX)} cy={sy(peakValue)} r={3} fill={stroke} />
          <text
            x={sx(peakX)}
            y={sy(peakValue) + (kind === 'moment' ? 14 : -7)}
            fontSize={10}
            textAnchor="middle"
            fill={stroke}
            className="tabular"
          >
            {peakValue.toFixed(1)}
          </text>

          {/* Probe cursor */}
          {probe && (
            <line
              x1={sx(probe.x)}
              y1={PAD.top}
              x2={sx(probe.x)}
              y2={HEIGHT - PAD.bottom}
              stroke="var(--diagram-axis)"
              strokeWidth={1}
              strokeDasharray="3 3"
            />
          )}

          <text x={6} y={baseline + 4} fontSize={10} fill="var(--diagram-axis)">
            0
          </text>
          <text
            x={WIDTH - PAD.right}
            y={PAD.top - 8}
            fontSize={10}
            textAnchor="end"
            fill="var(--diagram-axis)"
          >
            {unit}
          </text>
        </svg>
      </div>

      {/* Data-table fallback. A diagram alone is not accessible, and this is also the
          fastest way to read an exact ordinate (14.5). */}
      <details className="mt-1">
        <summary className="cursor-pointer text-xs text-text-muted">
          {t('diagram.show_data')}
        </summary>
        <div className="scroll-x mt-1">
          <table id={tableId} className="w-full min-w-[20rem] border-collapse text-xs">
            <thead>
              <tr className="border-b border-border text-text-muted">
                <th scope="col" className="py-1 text-start font-medium">
                  x [m]
                </th>
                <th scope="col" className="py-1 text-end font-medium">
                  {unit}
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Every fifth sample: the full 41-point array is noise in a table. */}
              {data.x_mm
                .map((x, index) => ({ x, value: values[index] ?? 0, index }))
                .filter(({ index }) => index % 5 === 0 || index === data.x_mm.length - 1)
                .map(({ x, value }) => (
                  <tr key={x} className="border-b border-border">
                    <td className="tabular py-1">{(x / 1000).toFixed(2)}</td>
                    <td className="tabular py-1 text-end">{value.toFixed(1)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </details>
    </figure>
  )
}
